import {
  CandidateLogoutResponseSchema,
  CandidateSessionResponseSchema,
  CandidateVerificationRequestResponseSchema,
  ResumePersonalizationResponseSchema,
  type CandidateEditableProfileDto,
  type CandidateLogoutResponseDto,
  type CandidateSessionResponseDto,
  type CandidateVerificationRequestResponseDto,
  type ResumeUploadSource,
  type ResumePersonalizationResponseDto,
} from "@artha/api-contracts";
import { API_ROUTES } from "@artha/types";
import { apiFetch } from "@/lib/apiClient";

type ApiEnvelope<T> = T | { data: T };

export class PersonalizationApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "PersonalizationApiError";
  }
}

const unwrap = <T>(payload: ApiEnvelope<T> | undefined): T | undefined =>
  payload && typeof payload === "object" && "data" in payload
    ? (payload as { data: T }).data
    : (payload as T | undefined);

const getApiError = (payload: unknown, fallback: string) => {
  if (!payload || typeof payload !== "object") {
    return new PersonalizationApiError(fallback);
  }
  const response = payload as {
    message?: unknown;
    code?: unknown;
    error?: { message?: unknown; code?: unknown };
  };
  const message = response.error?.message ?? response.message;
  const code = response.error?.code ?? response.code;
  return new PersonalizationApiError(
    typeof message === "string" && message ? message : fallback,
    typeof code === "string" && code ? code : undefined,
  );
};

export const uploadResumeForPersonalization = async (input: {
  file: File;
  creatorId: string;
  posthogDistinctId: string;
  externalId?: string;
  source?: ResumeUploadSource;
  jobId?: string;
  refId?: string;
}): Promise<ResumePersonalizationResponseDto> => {
  const body = new FormData();
  body.set("resume", input.file);
  body.set("creatorId", input.creatorId);
  body.set("posthogDistinctId", input.posthogDistinctId);
  body.set("source", input.source || "jobs_page");
  if (input.externalId) body.set("externalId", input.externalId);
  if (input.jobId) body.set("jobId", input.jobId);
  if (input.refId) body.set("refId", input.refId);

  const response = await apiFetch<
    ApiEnvelope<ResumePersonalizationResponseDto>
  >(API_ROUTES.JOBS.PERSONALIZATION_RESUME, { method: "POST", body });
  const result = unwrap(response.data);
  if (!response.ok || !result) throw new Error("Unable to process this resume");
  return ResumePersonalizationResponseSchema.parse(result);
};

export const requestCandidateVerification = async (input: {
  email?: string;
  externalId?: string;
  posthogDistinctId?: string;
  editableProfile?: CandidateEditableProfileDto;
  creatorId?: string;
  creatorUsername?: string;
  source?: ResumeUploadSource;
  jobId?: string;
  refId?: string;
  fitAttemptId?: string;
}): Promise<CandidateVerificationRequestResponseDto> => {
  const response = await apiFetch<
    ApiEnvelope<CandidateVerificationRequestResponseDto>
  >(API_ROUTES.JOBS.PERSONALIZATION_REQUEST_CODE, {
    method: "POST",
    body: JSON.stringify(input),
  });
  const result = unwrap(response.data);
  if (!response.ok || !result) {
    throw getApiError(response.data, "Unable to send verification code");
  }
  return CandidateVerificationRequestResponseSchema.parse(result);
};

export const verifyCandidateCode = async (input: {
  email: string;
  code: string;
  externalId?: string;
  posthogDistinctId?: string;
  fitAttemptId?: string;
}): Promise<CandidateSessionResponseDto> => {
  const response = await apiFetch<ApiEnvelope<CandidateSessionResponseDto>>(
    API_ROUTES.JOBS.PERSONALIZATION_VERIFY_CODE,
    { method: "POST", body: JSON.stringify(input) },
  );
  const result = unwrap(response.data);
  if (!response.ok || !result) {
    throw getApiError(response.data, "Invalid or expired verification code");
  }
  return CandidateSessionResponseSchema.parse(result);
};

export const fetchCandidateSession =
  async (): Promise<CandidateSessionResponseDto> => {
    const response = await apiFetch<ApiEnvelope<CandidateSessionResponseDto>>(
      API_ROUTES.JOBS.PERSONALIZATION_SESSION,
    );
    const result = unwrap(response.data);
    if (!response.ok || !result) {
      throw new Error("Unable to restore candidate session");
    }
    return CandidateSessionResponseSchema.parse(result);
  };

export const logoutCandidate =
  async (): Promise<CandidateLogoutResponseDto> => {
    const response = await apiFetch<ApiEnvelope<CandidateLogoutResponseDto>>(
      API_ROUTES.JOBS.PERSONALIZATION_LOGOUT,
      { method: "POST" },
    );
    const result = unwrap(response.data);
    if (!response.ok || !result) {
      throw new Error("Unable to log out");
    }
    return CandidateLogoutResponseSchema.parse(result);
  };
